/**
 * @file decision-form/cert-template.ts
 * @description 證書範本註冊表（需求清單序 153：6 種業務範本）+ 證書類型自動推斷。
 *
 * 下載範本依需求合併為 6 種：
 *   ① 有機／轉型期（個別）   organic_individual
 *   ② 有機／轉型期（集團）   organic_group
 *   ③ 有機加分流             organic_processing
 *   ④ 產銷履歷（個別）       tap_individual
 *   ⑤ 產銷履歷（集團）       tap_group
 *   ⑥ 產銷履歷（加分流）     tap_processing
 *
 * 推斷輸入：
 * - scheme：案件驗證方案（TOP=有機、TAP=產銷、ECO 暫回落 TOP）。
 * - option：驗證選項（individual 個別 / group 集團，來自 applicationDetails.verificationOption）。
 * - categories：驗證類別陣列（cases.verificationCategories，如 ["農場","加工場"]）；含加工場
 *   → 有機加工證書（加工證書在需求裡不分個別 / 集團）。
 * - isTransition：是否轉型期（有機轉型證書）。可由申請明細的轉型期面積 > 0 或類別含「轉型」判斷，
 *   由呼叫端算好傳入本純函式。
 *
 * 本檔為純常量 + 純函式，前後端共用；不讀取 DB / 不依賴 Date.now()，便於測試。
 * 實體 docx 表樣待業務提供；此處先落「類型枚舉 + 標籤 + 範本 key（templateKey）」，
 * 每種類型預留一個 templateKey，未來接實體 docx 渲染時按 key 對應範本檔。
 */
/**
 * 證書類型枚舉。
 * organic_transition_* 保留為存量資料兼容值，產出文件時分別映射到合併後的
 * 有機個別／集團範本；新業務範本另補 tap_processing。
 */
export const CERT_TYPES = [
    'organic_individual',
    'organic_transition_individual',
    'organic_group',
    'organic_transition_group',
    'organic_processing',
    'tap_individual',
    'tap_group',
    'tap_processing',
];
/**
 * 證書範本註冊表（6 種）。順序即前端展示順序。
 * templateKey 命名 `cert-<type>`，實體 docx 落地時據此對應範本檔。
 */
export const CERT_TEMPLATES = [
    {
        type: 'organic_individual',
        label: '有機／轉型期證書（個別）',
        scheme: 'TOP',
        templateKey: 'cert-organic-individual',
    },
    {
        type: 'organic_group',
        label: '有機／轉型期證書（集團）',
        scheme: 'TOP',
        templateKey: 'cert-organic-group',
    },
    {
        type: 'organic_processing',
        label: '有機加分流證書（個別／集團）',
        scheme: 'TOP',
        templateKey: 'cert-organic-processing',
    },
    {
        type: 'tap_individual',
        label: '產銷證書（個別）',
        scheme: 'TAP',
        templateKey: 'cert-tap-individual',
    },
    {
        type: 'tap_group',
        label: '產銷證書（集團）',
        scheme: 'TAP',
        templateKey: 'cert-tap-group',
    },
    {
        type: 'tap_processing',
        label: '產銷履歷證書（加分流）',
        scheme: 'TAP',
        templateKey: 'cert-tap-processing',
    },
];
/** 證書類型 → 中文標籤。 */
export const CERT_TYPE_LABELS = {
    organic_individual: '有機／轉型期證書（個別）',
    organic_transition_individual: '有機／轉型期證書（個別）',
    organic_group: '有機／轉型期證書（集團）',
    organic_transition_group: '有機／轉型期證書（集團）',
    organic_processing: '有機加分流證書（個別／集團）',
    tap_individual: '產銷履歷證書（個別）',
    tap_group: '產銷履歷證書（集團）',
    tap_processing: '產銷履歷證書（加分流）',
};
/** 依證書類型取範本定義（含 templateKey）。 */
export function getCertTemplate(type) {
    const normalizedType = type === 'organic_transition_individual'
        ? 'organic_individual'
        : type === 'organic_transition_group'
            ? 'organic_group'
            : type;
    const def = CERT_TEMPLATES.find((t) => t.type === normalizedType);
    // 理論上 type 恆在枚舉內；防禦性回落第一個範本。
    return def ?? CERT_TEMPLATES[0];
}
/**
 * 判斷驗證類別是否含加工場（有機加工證書）。
 * 兼容「加工場 / 加工廠 / 加工業者 / 加工」等寫法。
 */
function hasProcessingCategory(categories) {
    if (!categories?.length)
        return false;
    return categories.some((c) => typeof c === 'string' && c.includes('加工'));
}
/**
 * 依（方案 / 選項 / 類別 / 是否轉型）自動推斷 7 種證書類型之一（純函式）。
 *
 * 推斷規則：
 * - TAP 產銷：含加工類別 → tap_processing；否則個別／集團分流。
 * - TOP 有機（ECO 回落 TOP）：
 *   - 含加工類別 → organic_processing（有機加工，不分個別 / 集團）。
 *   - 否則按 個別 / 集團 × 是否轉型 四選一。
 *
 * @param input 見 {@link ResolveCertTypeInput}
 * @returns 推斷出的證書類型（前端可再手動修正）
 */
export function resolveCertType(input) {
    const isGroup = input.option === 'group';
    // 產銷履歷 TAP：只分個別 / 集團
    if (input.scheme === 'TAP') {
        if (hasProcessingCategory(input.categories))
            return 'tap_processing';
        return isGroup ? 'tap_group' : 'tap_individual';
    }
    // 有機 TOP（ECO 回落有機）：加工優先，其次個別 / 集團 × 轉型
    if (hasProcessingCategory(input.categories)) {
        return 'organic_processing';
    }
    if (input.isTransition) {
        return isGroup ? 'organic_transition_group' : 'organic_transition_individual';
    }
    return isGroup ? 'organic_group' : 'organic_individual';
}
